import { useEffect, useRef, useState, useCallback } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import noop from 'lodash/noop';
import { Row } from 'talent_matching/types';
import { SidePanelHandle } from 'shared/components/side_panel';
import { CellParamsDef } from '@grnhse/seedling/lib/birch/components/table';

const getCandidatePrefetchBatch = (
  rows: Row[],
  selectedRowIndex: number,
  batchRadius: number
) => {
  const startIndex = Math.max(0, selectedRowIndex - batchRadius);
  const endIndex = Math.min(rows.length, selectedRowIndex + batchRadius + 1);

  return [
    ...rows.slice(startIndex, selectedRowIndex),
    ...rows.slice(selectedRowIndex + 1, endIndex),
  ];
};

type UseSidePanelOptions = {
  rows: Row[];
  preSelectedRow?: Row | null;
  setPreSelectedRow?: (row: Row | null) => void;
};

const useSidePanel = ({
  rows,
  preSelectedRow = null,
  setPreSelectedRow = noop,
}: UseSidePanelOptions) => {
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(!!preSelectedRow);
  const [selectedRow, setSelectedRow] = useState<Row | null>(null);

  const selectedRowIndex =
    selectedRow && rows.findIndex((row) => row.id === selectedRow.id);
  const previousRow =
    selectedRowIndex !== null && selectedRowIndex > 0
      ? rows[selectedRowIndex - 1]
      : null;
  const nextRow =
    selectedRowIndex !== null && selectedRowIndex < rows.length - 1
      ? rows[selectedRowIndex + 1]
      : null;
  const candidatePrefetchBatch =
    selectedRowIndex !== null
      ? getCandidatePrefetchBatch(rows, selectedRowIndex, 2)
      : [];

  const popoverRef = useRef<SidePanelHandle | null>(null);
  // The grid row/cell that opened the panel, so focus can return to it on close.
  const triggerElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (preSelectedRow && rows.some((row) => row.id === preSelectedRow.id)) {
      setSelectedRow(preSelectedRow);
      setIsPanelOpen(true);
      setPreSelectedRow(null);
    }
  }, [rows, preSelectedRow, setPreSelectedRow]);

  // When the panel closes, return focus to the row/cell that opened it (ARIA grid
  // pattern). Guard on document.contains so we never focus a removed row.
  const wasPanelOpenRef = useRef(isPanelOpen);
  useEffect(() => {
    if (wasPanelOpenRef.current && !isPanelOpen) {
      const trigger = triggerElementRef.current;
      if (trigger && document.contains(trigger)) {
        trigger.focus();
      }
      triggerElementRef.current = null;
    }
    wasPanelOpenRef.current = isPanelOpen;
  }, [isPanelOpen]);

  const handlePanelToggle = useCallback(() => {
    if (isPanelOpen) {
      popoverRef.current?.close();
    } else {
      setIsPanelOpen(true);
    }
  }, [isPanelOpen]);

  const handleRowClick = useCallback(
    (row: Row) => {
      if (!row || !row.id) {
        return;
      }

      // Capture the triggering row/cell on a fresh open so focus can return to it
      // on close. Skip while already open (candidate navigation) to keep the
      // original entry point rather than a toolbar button.
      if (!isPanelOpen) {
        const active = document.activeElement;
        triggerElementRef.current =
          active instanceof HTMLElement &&
          active.closest('[role="row"][data-id]')
            ? active
            : null;
      }

      if (selectedRow?.id === row.id) {
        if (isPanelOpen) {
          popoverRef.current?.close();
        } else {
          setIsPanelOpen(true);
        }
      } else {
        setSelectedRow(row);
        setIsPanelOpen(true);
      }
    },
    [selectedRow?.id, isPanelOpen]
  );

  // ARIA grid pattern: Enter on a focused table row opens the side panel.
  // The grid (MUI) already handles arrow-key cell/row navigation and Tab-to-exit.
  const handleGridKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Enter') {
        return;
      }

      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      // Let actionable cell content (links, buttons, inputs) handle Enter itself.
      if (
        target.closest(
          'a, button, input, select, textarea, [role="button"], [contenteditable="true"]'
        )
      ) {
        return;
      }

      const rowElement = target.closest('[role="row"][data-id]');
      const rowId = rowElement?.getAttribute('data-id');
      if (!rowId) {
        return;
      }

      const row = rows.find((candidate) => String(candidate.id) === rowId);
      if (!row) {
        return;
      }

      event.preventDefault();
      handleRowClick(row);
    },
    [rows, handleRowClick]
  );

  const navigateAfterRemoval = useCallback(() => {
    if (nextRow || previousRow) {
      setPreSelectedRow(nextRow || previousRow || null);
    } else {
      setIsPanelOpen(false);
      setSelectedRow(null);
    }
  }, [nextRow, previousRow, setPreSelectedRow]);

  const getRowClassName = useCallback(
    (params: CellParamsDef) => {
      return isPanelOpen && selectedRow?.id === params.row.id
        ? 'highlighted-row'
        : '';
    },
    [isPanelOpen, selectedRow?.id]
  );

  return {
    isPanelOpen,
    setIsPanelOpen,
    selectedRow,
    selectedRowIndex,
    popoverRef,
    previousRow,
    nextRow,
    candidatePrefetchBatch,
    handlePanelToggle,
    handleRowClick,
    handleGridKeyDown,
    getRowClassName,
    navigateAfterRemoval,
  };
};

export default useSidePanel;
